import React, {useEffect, useState} from 'react';
import AddIcon from '@mui/icons-material/Add';
import {Box, Button, Stack, Typography} from '@mui/material';

import {styled} from '@mui/material/styles';
import {useParams} from "react-router-dom";
import Section from "./Section";
import {canEditBoard, TaskSearchResult} from "../../types/task";
import {TaskSearch} from "./TaskSearch";
import {useBoard, useCreateSection} from '../../data/board';

// What the board should bring into view after a search result is picked. `nonce` makes picking
// the same result twice register as a new request.
export type TaskFocus = { taskId: string, seriesId?: string, nonce: number };

const HIGHLIGHT_MS = 1800;

export default function Board() {
    const { boardId } = useParams<{ boardId: string }>();

    const {data: board} = useBoard(boardId);
    const {mutate: createSection} = useCreateSection(boardId);

    const handleColumnSubmit = async () => {
        createSection("Untitled section");
    };


    const canEdit = canEditBoard(board.viewerRole);

    const [focus, setFocus] = useState<TaskFocus | undefined>();

    function focusSearchResult(result: TaskSearchResult) {
        setFocus({taskId: result.id, seriesId: result.seriesId, nonce: Date.now()});
    }

    // Scroll to the picked task's card and flash it. A series card has to expand first, so the
    // card may not exist yet: look for it for up to two seconds.
    useEffect(() => {
        if (!focus) return;

        let attempts = 0;
        const timer = setInterval(() => {
            const card = document.querySelector<HTMLElement>(`[data-task-id="${focus.taskId}"]`);
            // A card inside a series is still sliding into place until its Collapse has finished
            // opening; scrolling earlier would aim at where the card is about to move from.
            const collapse = card?.closest('.MuiCollapse-root');
            const isSettled = !collapse || collapse.classList.contains('MuiCollapse-entered');
            if (card && isSettled) {
                clearInterval(timer);
                card.scrollIntoView({block: 'center', behavior: 'smooth'});
                card.animate(
                    [{boxShadow: '0 0 0 4px rgba(0, 137, 123, 0.55)'}, {boxShadow: '0 0 0 4px rgba(0, 137, 123, 0)'}],
                    {duration: HIGHLIGHT_MS, easing: 'ease-out'},
                );
            } else if (++attempts >= 40) {
                clearInterval(timer);
            }
        }, 50);

        return () => clearInterval(timer);
    }, [focus]);

    const sectionTitles = Object.fromEntries(
        board.sectionOrder.map((sectionId) => [sectionId, board.sectionsById[sectionId]?.title ?? 'Untitled section'])
    );

    return (
        <Stack spacing={2}>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={2}
                alignItems={{xs: 'stretch', sm: 'flex-start'}}
                justifyContent="space-between"
            >
                <TaskSearch boardId={boardId} sectionTitles={sectionTitles} onSelect={focusSearchResult} />

                {canEdit && (
                    <Button startIcon={<AddIcon/>} type="button" variant="contained" onClick={handleColumnSubmit}>
                        Add section
                    </Button>
                )}
            </Stack>

            {board.sectionOrder.length === 0 && (
                <Typography color="text.secondary" sx={{py: 4, textAlign: 'center'}}>
                    {canEdit ? 'No sections yet. Add one to start organising tasks.' : 'This board has no sections yet.'}
                </Typography>
            )}

            {/* Fixed-width columns side by side; the row scrolls sideways when they don't all fit. */}
            <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start', overflowX: 'auto', pb: 2, scrollSnapType: 'x proximity'}}>
                {board.sectionOrder.map((sectionId) => {
                    const section = board.sectionsById[sectionId];
                    return (
                        <Section key={sectionId} id={sectionId} title={section.title} boardId={boardId} viewerRole={board.viewerRole} focus={focus} />
                    );
                })}
            </Box>
        </Stack>
    );
};
